import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Select, Button, Input } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AuthContext from "../../context/AuthContext";
import { useTickets } from "../../queries/tickets";
import { useProjects } from "../../queries/projects";
import { TicketProps, ProjectProps, Priority, Severity } from "../../types/interfaces";
import { SEVERITY_OPTIONS, PRIORITY_OPTIONS, SEVERITY_ORDER } from "../../utils/levels";
import { formatMinutes } from "../../utils/duration";
import { firstLine } from "../../utils/text";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { StatusTag, PriorityChip, SeverityChip } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import { NewTicketModal } from "../../components/pages/tickets/NewTicketModal";

/**
 * The list archetype: sticky page header, a filter toolbar with a live count,
 * then one full-bleed table. No Card wrapper — the table's own rules are the
 * container.
 */
export const TicketListPage = () => {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const [projectId, setProjectId] = useState<string | undefined>();
  const [solved, setSolved] = useState<boolean | undefined>(false);
  const [severity, setSeverity] = useState<Severity | undefined>();
  const [priority, setPriority] = useState<Priority | undefined>();
  const [query, setQuery] = useState("");
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  const { data: tickets, isLoading, isError, error } = useTickets({ projectId, solved, severity, priority });
  const { data: projects } = useProjects();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tickets || [])
      .filter((t: TicketProps) => (q ? t.subject.toLowerCase().includes(q) : true))
      .sort((a: TicketProps, b: TicketProps) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  }, [tickets, query]);

  return (
    <PageShell
      kind="list"
      kicker="Support"
      title={solved ? "Solved tickets" : "Open tickets"}
      subtitle={
        userData?.role === "requester"
          ? "Anything you've raised with us, and where it stands."
          : undefined
      }
      actions={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewTicketOpen(true)}>
          New ticket
        </Button>
      }
    >
      <Toolbar count={rows.length} unit="tickets">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--osi-text-faint)" }} />}
          placeholder="Search subjects"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
        {userData?.role !== "requester" && (
          <Select
            allowClear
            style={{ width: 200 }}
            placeholder="All projects"
            value={projectId}
            onChange={setProjectId}
            options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
          />
        )}
        <Select allowClear style={{ width: 150 }} placeholder="Any severity" value={severity} onChange={setSeverity} options={SEVERITY_OPTIONS} />
        <Select allowClear style={{ width: 150 }} placeholder="Any priority" value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
        <Select
          style={{ width: 140 }}
          value={solved}
          onChange={setSolved}
          options={[
            { label: "Open", value: false },
            { label: "Solved", value: true },
          ]}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<TicketProps>
          rowKey="_id"
          size="small"
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25, size: "small" } : false}
          onRow={(ticket) => ({
            onClick: () => navigate(`/support/tickets/${ticket._id}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <EmptyState
                title={solved ? "No solved tickets yet" : "No open tickets"}
                description={
                  solved
                    ? "Tickets move here once they're marked solved."
                    : "Nothing is waiting on you. New requests will appear here first."
                }
                actions={
                  !solved && (
                    <Button type="primary" onClick={() => setNewTicketOpen(true)}>
                      Raise a ticket
                    </Button>
                  )
                }
              />
            ),
          }}
          columns={[
            {
              title: "Subject",
              dataIndex: "subject",
              render: (subject: string) => (
                <span style={{ fontWeight: 500, display: "block", maxWidth: 520, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={subject}>
                  {firstLine(subject)}
                </span>
              ),
            },
            {
              title: "Severity",
              dataIndex: "severity",
              width: 120,
              render: (value?: Severity) => <SeverityChip severity={value || "minor"} />,
            },
            {
              title: "Priority",
              dataIndex: "priority",
              width: 110,
              render: (value?: Priority) => <PriorityChip priority={value || "medium"} />,
            },
            {
              title: "First response",
              dataIndex: "firstResponseAt",
              width: 130,
              align: "right",
              render: (_: string | null, t: TicketProps) => {
                const end = t.firstResponseAt ? dayjs(t.firstResponseAt) : dayjs();
                const text = formatMinutes(Math.max(0, end.diff(dayjs(t.createdAt), "minute")));
                return t.firstResponseAt
                  ? <span className="osi-num">{text}</span>
                  : <span className="osi-num" style={{ color: "var(--osi-warn)" }}>Waiting {text}</span>;
              },
            },
            {
              title: "Status",
              dataIndex: "solved",
              width: 120,
              render: (v: boolean) =>
                v ? <StatusTag label="Solved" tone="ok" dot /> : <StatusTag label="Open" tone="warn" dot />,
            },
            {
              title: "Replies",
              dataIndex: "comments",
              width: 90,
              align: "right",
              render: (comments: TicketProps["comments"]) => (
                <span className="osi-num" style={{ color: comments.length ? undefined : "var(--osi-text-faint)" }}>
                  {comments.length}
                </span>
              ),
            },
            {
              title: "Last activity",
              dataIndex: "updatedAt",
              width: 140,
              align: "right",
              render: (v: string) => (
                <span className="osi-num" style={{ color: "var(--osi-text-muted)" }}>
                  {dayjs(v).format("D MMM YYYY")}
                </span>
              ),
            },
          ]}
        />
      </DisplayComponent>
      <NewTicketModal open={newTicketOpen} onClose={() => setNewTicketOpen(false)} />
    </PageShell>
  );
};
